import api from "./api.service";
import { adminTokenService } from "./admin.token.service";
import { GetOrderResponse } from "./order.service";

export interface ExternalServiceDetail {
    serviceName: string;
    codeRef?: string;
    externalReference?: string;
}

export interface AdminOrder {
    id: number;
    orderNumber: string;
    tracking: string;
    trackingCarrier: string;
    idStatus: number;
    status: string;
    idCustomer: number;
    customer: {
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        dni: string;
        kindOfDNI: string;
    };
    creationDate: string;
    receiver: {
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        dni: string;
        kindOfDNI: string;
    } | null;
    idCarrier: number;
    carrier: string;
    serviceType: string[];
    externalServices?: ExternalServiceDetail[];
    carrierService: string;
    paid?: boolean;
    voided?: boolean;
    refunded?: boolean;
    finished?: boolean;
    totalPaid?: number;
    margin?: {
        totalPaid?: number;
        upsCost: number;
        upsCharged: number;
        upsGross?: number;
        upsArrastreCharged?: number;
        upsPickupCharged?: number;
        courierCharged: number;
        courierTotal?: number;
        courierCommission: number;
        logistica: number;
        gross: number;
        grossPercent?: number;
    } | null;}

export interface GetAdminOrdersParams {
    page?: number;
    pageSize?: number;
    status?: number | number[];
    SearchStringCustomer?: string;
    SearchStringOrder?: string;
    start?: string;
    end?: string;
}

export interface GetAdminOrdersResponse {
    statusCode: number;
    message: string;
    data: AdminOrder[];
    pagination: {
        totalCount: number;
        page: number;
        pageSize: number;
        totalPages: number;
    };
}

class AdminOrderService {
    /**
     * Fetches a list of orders for administration purposes.
     * @param params Filtering and pagination parameters.
     * @returns A promise with the orders data and pagination info.
     */
    async getOrders(params?: GetAdminOrdersParams): Promise<GetAdminOrdersResponse> {
        const token = adminTokenService.getAdminToken();
        try {
            const queryParams = new URLSearchParams();
            if (params) {
                if (params.page !== undefined) queryParams.append("page", params.page.toString());
                if (params.pageSize !== undefined) queryParams.append("pageSize", params.pageSize.toString());
                if (params.SearchStringCustomer) queryParams.append("SearchStringCustomer", params.SearchStringCustomer);
                if (params.SearchStringOrder) queryParams.append("SearchStringOrder", params.SearchStringOrder);
                if (params.start) queryParams.append("start", params.start);
                if (params.end) queryParams.append("end", params.end);

                if (Array.isArray(params.status)) {
                    params.status.forEach((val) => queryParams.append("status", val.toString()));
                } else if (params.status !== undefined) {
                    queryParams.append("status", params.status.toString());
                }
            }

            const response = await api.get<GetAdminOrdersResponse>(
                `/admin/get-orders?${queryParams.toString()}`,
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching admin orders:", error);
            throw error;
        }
    }

    /**
     * Fetches the detailed resume of a specific order by ID.
     * @param id The ID of the order.
     */
    async getOrderResume(id: number): Promise<GetOrderResponse> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.get<GetOrderResponse>(
                "/admin/get-order-resume",
                {
                    params: { id },
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching admin order resume:", error);
            throw error;
        }
    }

    /**
     * Cancels an order.
     * @param data ID of the order to cancel.
     */
    async cancelOrder(data: { Id: number }): Promise<{ statusCode: number; message: string }> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.post<{ statusCode: number; message: string }>(
                "/admin/cancel-order",
                data,
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error cancelling admin order:", error);
            throw error;
        }
    }

    /**
     * Processes a manual refund for a cancelled order.
     * @param data ID of the order to refund.
     */
    async refundOrder(data: { Id: number }): Promise<{ statusCode: number; message: string }> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.post<{ statusCode: number; message: string }>(
                "/admin/refund-order", data, { headers: { Authorization: `${token}` } }
            );
            return response.data;
        } catch (error) {
            console.error("Error refunding admin order:", error);
            throw error;
        }
    }

    /** Downloads an Excel report of orders matching the specified filters. */
    async downloadOrdersExcel(params?: Omit<GetAdminOrdersParams, "page" | "pageSize">): Promise<Blob> {
        const token = adminTokenService.getAdminToken();
        try {
            const queryParams = new URLSearchParams();
            if (params) {
                if (params.SearchStringCustomer) queryParams.append("SearchStringCustomer", params.SearchStringCustomer);
                if (params.SearchStringOrder) queryParams.append("SearchStringOrder", params.SearchStringOrder);
                if (params.start) queryParams.append("start", params.start);
                if (params.end) queryParams.append("end", params.end);
                if (Array.isArray(params.status)) params.status.forEach((val) => queryParams.append("status", val.toString()));
                else if (params.status !== undefined) queryParams.append("status", params.status.toString());
            }
            const response = await api.get(`/admin/export-orders-excel?${queryParams.toString()}`, {
                headers: { Authorization: `${token}` }, responseType: "blob",
            });
            return response.data;
        } catch (error) {
            console.error("Error downloading admin orders excel:", error);
            throw error;
        }
    }
}

export const adminOrderService = new AdminOrderService();
